/* eslint react/destructuring-assignment: 0 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { matchPath } from 'react-router-dom';
import { HotKeys } from 'react-hotkeys';
import Preloader from '@omilia/preloader';
import { isDirty } from 'redux-form';

import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import _merge from 'lodash/merge';

import { paginationOptionsType, contextType } from 'types';

import FORM_NAMES from 'constants/formNames';
import COMBOS from 'constants/keyboardShortcutsCombos';
import BLOCKS from 'constants/defaultContextBlocks';

// TODO: get required actions & selectors only
import * as actions from 'store/services/contexts/actions';
import * as selectors from 'store/services/contexts/selectors';

import withPartialImportModal from 'components/withPartialImportModal';
import { withExportingModal } from 'components/PartialExportModal';
import Splitter from 'components/Splitter';
import UnsavedChangesModal from 'components/UnsavedChangesModal';
import Pagination from 'components/Pagination';
import UnitsSearchResult from 'components/Units/UnitsSearchResult';
import NoUnits from 'components/Units/NoUnits';
import ListHeaderOptions from 'components/ListHeaderOptions';

import ContextsList from './components/ContextsList';
import ContextInspect from './components/ContextInspect';
import CreateNewContextModal from './components/CreateNewContextModal';

import './style.scss';

const HOTKEYS_MAP = {
  create: COMBOS.createNew,
};

export class Contexts extends Component {
  handleHotKeys = {
    create: this.handleOpenNewContextModal,
  };

  static propTypes = {
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
    match: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }).isRequired,
    partialExport: PropTypes.func.isRequired,
    addContext: PropTypes.func,
    checkContextName: PropTypes.func,
    contexts: PropTypes.arrayOf(contextType),
    count: PropTypes.number,
    currentContext: contextType,
    currentContextIndex: PropTypes.number,
    deleteContext: PropTypes.func,
    deselectContext: PropTypes.func,
    dirty: PropTypes.bool,
    getContexts: PropTypes.func,

    isLoading: PropTypes.bool,
    isShowing: PropTypes.bool,
    paginationOptions: paginationOptionsType,

    selectContext: PropTypes.func,

    updateContext: PropTypes.func,
  };

  state = {
    isNewContentModalOpen: false,
    isUnsavedChangesModalOpen: false,
    selectedContextId: undefined,
  };

  componentDidMount = () => {
    this.props.getContexts({
      page: 1,
      name__icontains: '',
    });
  };

  componentWillUnmount = () => {
    this.props.deselectContext();
  };

  handleInspectorSave = context => {
    const { currentContext } = this.props;

    this.props.updateContext(
      currentContext.id,
      // FIXME: Seems action should do this (merge data and meta-data)
      _merge(currentContext, context),
    );
  };

  handleInspectorDestroy = () => {
    this.props.history.push(this.props.match.url);
    this.props.deleteContext(this.props.currentContext.id);
  };

  handleOpenNewContextModal = () => {
    this.setState({
      isNewContentModalOpen: true,
    });
  };

  handleCloseNewContextModal = () => {
    this.setState({
      isNewContentModalOpen: false,
    });
  };

  handleSaveNewContextModal = async ({ contextName }) => {
    this.handleCloseNewContextModal();
    const id = await this.props.addContext({
      name: contextName,
      // TODO: move default blocks to action logic
      blocks: BLOCKS,
    });

    this.handleSelectListItem({ id });
  };

  handleSaveUnsavedChanges = async () => {
    await this.submit();
  };

  handleOpenUnsavedChangesModal = () => {
    this.setState({
      isUnsavedChangesModalOpen: true,
    });
  };

  handleCloseUnsavedChangesModal = () => {
    this.setState({
      isUnsavedChangesModalOpen: false,
      selectedContextId: undefined,
    });
  };

  handleForceCloseInspectorTab = () => {
    this.props.deselectContext();
    this.handleCloseUnsavedChangesModal();

    this.props.history.push(this.props.match.url);
  };

  handleInspectorClose = (isDirty, submit) => {
    if (isDirty) {
      this.submit = submit;
      this.handleOpenUnsavedChangesModal();

      return;
    }

    this.props.history.push(this.props.match.url);
  };

  handleSelectListItem = ({ id }) => {
    if (!this.props.dirty) {
      this.openNewContext(id);
    } else {
      this.setState({ selectedContextId: id }, () => {
        this.handleOpenUnsavedChangesModal();
      });
    }
  };

  openNewContext = id =>
    this.props.history.push(`${this.props.match.url}/${id}`);

  findItem = (id, contexts, currentContext) => {
    let item = _find(contexts, { id });

    if (!item && !_isEmpty(currentContext)) {
      item = currentContext;
    }

    return item;
  };

  handleSelectContext = id => {
    const item = this.findItem(
      id,
      this.props.contexts,
      this.props.currentContext,
    );

    if (item) {
      this.props.selectContext(item);
    } else {
      this.props.history.push('/not-found');
    }
  };

  handleMergeImport = () => {
    this.props.history.push(`${this.props.match.url}/import/merge`);
  };

  handleReplaceImport = () => {
    this.props.history.push(`${this.props.match.url}/import/replace`);
  };

  renderNewContextModal = () => {
    if (this.state.isNewContentModalOpen) {
      return (
        <CreateNewContextModal
          onCheckContextName={this.props.checkContextName}
          onClose={this.handleCloseNewContextModal}
          onSubmit={this.handleSaveNewContextModal}
        />
      );
    }

    return null;
  };

  renderList = () => {
    const {
      isLoading,
      contexts,
      getContexts,
      currentContext,
      currentContextIndex,
      paginationOptions,
    } = this.props;

    const contextsLength = contexts.length;

    if (isLoading) {
      return <Preloader />;
    }

    if (contextsLength > 0) {
      return (
        <ContextsList
          contexts={contexts}
          selectedContext={currentContext}
          selectedContextIndex={currentContextIndex}
          onDeselect={this.handleInspectorClose}
          onSelect={this.handleSelectListItem}
        />
      );
    }

    if (paginationOptions.name__icontains !== '') {
      return (
        <UnitsSearchResult
          items={contexts}
          searchTerm={paginationOptions.name__icontains}
          showAll={() =>
            getContexts({
              page: 1,
              name__icontains: '',
            })
          }
          unitName="context"
        />
      );
    }

    if (
      !contextsLength &&
      !isLoading &&
      paginationOptions.name__icontains === ''
    ) {
      return (
        <NoUnits
          openModal={this.handleOpenNewContextModal}
          unitName="contexts"
        />
      );
    }

    return null;
  };

  renderSplitPane = () => {
    const { isShowing, currentContext, contexts, location } = this.props;

    const match = matchPath(location.pathname, {
      exact: true,
      path: '/:company/:project/contexts/:context',
    });

    return (
      <Splitter split="horizontal">
        {this.renderList()}

        {match && contexts.length > 0 ? (
          <ContextInspect
            context={currentContext}
            id={match.params.context}
            isLoading={isShowing}
            // FIXME: selectContext before splitting
            onClose={this.handleInspectorClose}
            onDelete={this.handleInspectorDestroy}
            onSelect={this.handleSelectContext}
            onSubmit={this.handleInspectorSave}
          />
        ) : null}
      </Splitter>
    );
  };

  handleForceChangeInspectorTab = () => {
    this.handleCloseUnsavedChangesModal();
    this.openNewContext(this.state.selectedContextId);
    this.setState({ selectedContextId: undefined });
  };

  renderUnsavedChangesModal = () => {
    if (this.state.isUnsavedChangesModalOpen) {
      return (
        <UnsavedChangesModal
          isOpen
          onCancel={() => {
            this.handleCloseUnsavedChangesModal();
          }}
          onDiscard={
            this.state.selectedContextId
              ? this.handleForceChangeInspectorTab
              : this.handleForceCloseInspectorTab
          }
        />
      );
    }

    return null;
  };

  render() {
    const {
      partialExport,
      count,
      contexts,
      getContexts,
      paginationOptions,
    } = this.props;

    return (
      <HotKeys
        handlers={this.handleHotKeys}
        keyMap={HOTKEYS_MAP}
        styleName="contexts"
      >
        {this.renderNewContextModal()}
        {this.renderUnsavedChangesModal()}

        <ListHeaderOptions
          getItems={getContexts}
          items={contexts}
          modelName="context"
          openNewModal={this.handleOpenNewContextModal}
          searchTerm={paginationOptions.name__icontains}
          onExport={partialExport}
          onMergeImport={this.handleMergeImport}
          onReplaceImport={this.handleReplaceImport}
        />

        <div className="tabs-content right-tabs height-184">
          {this.renderSplitPane()}
        </div>

        {contexts.length > 0 && (
          <Pagination
            className="footer__pagination"
            count={count}
            getItems={getContexts}
            paginationOptions={paginationOptions}
            styleName="pagination"
          />
        )}
      </HotKeys>
    );
  }
}

const mapStateToProps = state => ({
  isExporting: selectors.getIsExporting(state),
  paginationOptions: selectors.getPaginationOptions(state),
  isLoading: selectors.getIsLoadingList(state),
  isShowing: selectors.getIsShowing(state),
  count: selectors.getContextsListCount(state),
  contexts: selectors.getContextsList(state),
  currentContext: selectors.getContext(state),
  currentContextIndex: selectors.getContextIndex(state),
  dirty: isDirty(FORM_NAMES.CONTEXTS.INSPECT)(state),
});

const mapDispatchToProps = {
  partialExport: actions.partialExportContext,
  cancelExport: actions.cancelExportContexts,
  getContexts: actions.getContextsList,
  addContext: actions.addContext,
  deleteContext: actions.deleteContext,
  selectContext: actions.selectContext,
  deselectContext: actions.deselectContext,
  updateContext: actions.updateContext,
  checkContextName: actions.checkContextName,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withPartialImportModal('contexts'),
  withExportingModal('Contexts'),
)(Contexts);
